import { useEffect, useMemo, useState } from 'react';
import DashboardChart from './DashboardChart';
import { ApexOptions } from 'apexcharts';
import { DashboardService } from './service';
import { useDashboardContext } from './Context';
import KpiCard from './KpiCard';
import { Percent, Receipt, ShoppingBag, TrendingUp } from 'lucide-react';

const service = new DashboardService();

const formatCurrency = (value: number) =>
    Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatPercent = (value: number) => `${Number(value || 0).toFixed(1)}%`;

const formatDate = (value?: string) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('pt-BR');
};

export default function SalesDashboard() {
    const { startAt, endAt } = useDashboardContext();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<any>(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const response = await service.getVendas({ startAt, endAt });
                setData(response.data);
            } catch (error) {
                console.error('Erro ao carregar dashboard de vendas', error);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [startAt, endAt]);

    const trend = useMemo(() => {
        const rows = data?.trend || [];
        const options: ApexOptions = {
            chart: { type: 'line', toolbar: { show: false } },
            stroke: { curve: 'smooth', width: 2.5 },
            colors: ['#007aff', '#34c759'],
            xaxis: {
                categories: rows.map((row: any) => row.date),
            },
            yaxis: {
                labels: {
                    formatter: (value: number) => Math.round(value).toString(),
                },
            },
            tooltip: {
                y: {
                    formatter: (value: number) => value.toFixed(2),
                },
            },
            grid: {
                borderColor: 'rgba(0,0,0,0.08)',
            },
        };

        const series = [
            {
                name: 'Receita',
                data: rows.map((row: any) => row.totalRevenue || 0),
            },
            {
                name: 'Quantidade',
                data: rows.map((row: any) => row.totalCount || 0),
            },
        ];

        return { options, series };
    }, [data]);

    if (!data) {
        return (
            <div className="card sf-dashboard-panel">
                <div className="card-body">{loading ? 'Carregando...' : 'Sem dados'}</div>
            </div>
        );
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                <KpiCard
                    title="Receita de vendas"
                    value={formatCurrency(data.summary.totalRevenue)}
                    delta={data.summary.totalRevenueDelta}
                    tone="success"
                    icon={TrendingUp}
                />
                <KpiCard
                    title="Qtd. de vendas"
                    value={data.summary.totalCount}
                    delta={data.summary.totalCountDelta}
                    tone="primary"
                    icon={ShoppingBag}
                />
                <KpiCard
                    title="Ticket médio"
                    value={formatCurrency(data.summary.ticketMedio)}
                    delta={data.summary.ticketMedioDelta}
                    tone="info"
                    icon={Receipt}
                />
                <KpiCard
                    title="Desconto total"
                    value={formatCurrency(data.summary.totalDiscount)}
                    delta={data.summary.totalDiscountDelta}
                    tone="warning"
                    icon={Percent}
                />
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <div className="card sf-dashboard-panel xl:col-span-2">
                    <div className="card-body">
                        <h3 className="text-lg font-semibold">Tendência de vendas</h3>
                        <DashboardChart options={trend.options} series={trend.series} type="line" height={260} />
                    </div>
                </div>

                <div className="card sf-dashboard-panel">
                    <div className="card-body">
                        <h3 className="text-lg font-semibold">Recorrências</h3>
                        <div className="mt-3 space-y-2 text-sm">
                            <div className="flex items-center justify-between">
                                <span>Ativas</span>
                                <strong>{data.summary.activeRecurringCount}</strong>
                            </div>
                            <div className="flex items-center justify-between">
                                <span>Execuções com sucesso</span>
                                <strong>{data.recurring.successExecutions}</strong>
                            </div>
                            <div className="flex items-center justify-between">
                                <span>Execuções com falha</span>
                                <strong>{data.recurring.failedExecutions}</strong>
                            </div>
                            <div className="flex items-center justify-between">
                                <span>Taxa de sucesso</span>
                                <strong>{formatPercent(data.summary.recurringExecutionSuccessRate)}</strong>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                <div className="card sf-dashboard-panel">
                    <div className="card-body">
                        <h3 className="text-lg font-semibold">Origem das vendas</h3>
                        <div className="overflow-x-auto mt-3">
                            <table className="table table-sm table-zebra">
                                <thead>
                                    <tr>
                                        <th>Origem</th>
                                        <th>Qtd</th>
                                        <th>Receita</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.sourceBreakdown || []).map((row: any) => (
                                        <tr key={row.source}>
                                            <td>{row.source}</td>
                                            <td>{row.totalCount}</td>
                                            <td>{formatCurrency(row.totalRevenue)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="card sf-dashboard-panel">
                    <div className="card-body">
                        <h3 className="text-lg font-semibold">Top produtos</h3>
                        <div className="overflow-x-auto mt-3">
                            <table className="table table-sm table-zebra">
                                <thead>
                                    <tr>
                                        <th>Produto</th>
                                        <th>Qtd</th>
                                        <th>Receita</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.topProducts || []).map((row: any) => (
                                        <tr key={row.itemId}>
                                            <td>{row.name}</td>
                                            <td>{row.quantity}</td>
                                            <td>{formatCurrency(row.revenue)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                <div className="card sf-dashboard-panel">
                    <div className="card-body">
                        <h3 className="text-lg font-semibold">Clientes que mais compram</h3>
                        <div className="overflow-x-auto mt-3">
                            <table className="table table-sm table-zebra">
                                <thead>
                                    <tr>
                                        <th>Cliente</th>
                                        <th>Pedidos</th>
                                        <th>Receita</th>
                                        <th>Última compra</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(data.topClients || []).map((row: any) => (
                                        <tr key={row.clientId}>
                                            <td>{row.name}</td>
                                            <td>{row.totalCount}</td>
                                            <td>{formatCurrency(row.totalRevenue)}</td>
                                            <td>{formatDate(row.lastPurchaseAt)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
